import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { io } from "socket.io-client";
import { useConfig } from "../../context/configContext";
import image from "../../assests/image.png";
import "../../App.css";
import "./index.css";
import { FaAngleUp } from "react-icons/fa";

const TEMP_UNIT = "\u00b0C";
const META_KEY = "_meta";
const DEFAULT_LIVE_DATA_TIMEOUT_MS = 15000;

const getLiveDataTimeout = () => {
  const configuredTimeout = Number(process.env.REACT_APP_LIVE_DATA_TIMEOUT_MS);
  return Number.isFinite(configuredTimeout) && configuredTimeout > 0
    ? configuredTimeout
    : DEFAULT_LIVE_DATA_TIMEOUT_MS;
};

const LIVE_DATA_TIMEOUT_MS = getLiveDataTimeout();

// camelCase converter
const simplifyData = (data) => {
  const simplified = {};

  Object.entries(data).forEach(([outerKey, innerObj]) => {
    if (typeof innerObj === "object" && innerObj !== null) {
      const newInner = {};

      Object.entries(innerObj).forEach(([key, value]) => {
        const lastPart = key.split("/").pop();

        const camelKey = lastPart.replace(/_([a-z])/g, (_, letter) =>
          letter.toUpperCase(),
        );

        let cleanValue = value;

        if (Array.isArray(value)) {
          cleanValue = {
            value: value[0],
            ts: value[1],
          };
        }

        newInner[camelKey] = cleanValue;
      });

      simplified[outerKey] = newInner;
    } else {
      simplified[outerKey] = innerObj;
    }
  });

  return simplified;
};

// Helper function to convert seconds to hh:mm
const formatSecondsToHHMM = (sec) => {
  if (sec === undefined || sec === null) return "";
  const hours = Math.floor(sec / 3600);
  const minutes = Math.floor((sec % 3600) / 60);
  const paddedHours = String(hours).padStart(2, "0");
  const paddedMinutes = String(minutes).padStart(2, "0");
  return `${paddedHours}:${paddedMinutes}`;
};

function formatLabel(text) {
  return text.replace(/([A-Z])/g, " $1").replace(/^./, (c) => c.toUpperCase());
}

const formatNumber = (value, unit = "") => {
  if (value == null || value === "" || Number.isNaN(value)) {
    return "";
  }
  const num = Number(value);
  if (Number.isNaN(num)) return value; // Number formatting
  const formatted = Number.isInteger(num) ? num : num.toFixed(2);
  return unit ? `${formatted} ${unit}` : formatted;
};

const formatValue = (value) => {
  if (value == null || value === "") return "N/A";
  if (typeof value === "object" && value !== null && "value" in value) {
    return formatValue(value.value);
  }
  if (typeof value === "object" && value !== null) {
    try {
      return JSON.stringify(value);
    } catch {
      return "N/A";
    }
  }
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return Number.isInteger(num) ? num : num.toFixed(2);
};

const ipAddress = process.env.REACT_APP_IP_ADDRESS || "";

const DataValue = ({ data, unit = "", isStale }) => {
  if (data === null || data === undefined) {
    return <></>;
  }
  const value =
    typeof data === "object" && data !== null && "value" in data
      ? data.value
      : data;
  return <span>{formatNumber(value, unit)}</span>;
};

const DispenserDetail = () => {
  const { station_id, dispensor_id } = useParams();
  const dispensorId = dispensor_id;
  const { config } = useConfig();
  const [showStopPopup, setShowStopPopup] = useState(false);
  const [selectedGun, setSelectedGun] = useState(null);
  const [showDispensor, setShowDispensor] = useState(false);
  const [data, setData] = useState(null);
  const [connected, setConnected] = useState(false);
  const [showGuns, setShowGuns] = useState(false);
  const [unitUpdatedAt, setUnitUpdatedAt] = useState({});
  const [now, setNow] = useState(() => Date.now());
  const selectedDispensor = config.dispensersData?.find(
    (d) => d.dispensor_id === dispensorId,
  );

  const gun1 = selectedDispensor?.guns?.[0]?.gun_id ?? null;
  const gun2 = selectedDispensor?.guns?.[1]?.gun_id ?? null;

  useEffect(() => {
    const socket = io(`${ipAddress}/dispensor`, {
      transports: ["websocket","polling"],
    });

    socket.on("connect", () => {
      setConnected(true);
      setData(null);
      setUnitUpdatedAt({});
      console.log("Socketio connected.")
      socket.emit("dispensor_id", {
        dispensor_id: dispensorId,
        station_id: station_id,
        gun1,
        gun2,
      });
    });

    socket.onAny((event, message) => {
      if (event === `dispensor_${dispensorId}_update`) {
        let simplifiedMessage = {};

        try {
          const parsedMessage =
            typeof message === "string" ? JSON.parse(message) : message;
          simplifiedMessage = simplifyData(parsedMessage || {});
        } catch (error) {
          console.error("Invalid dispenser payload:", error);
          return;
        }

        const receivedAt = Date.now();
        const meta = simplifiedMessage[META_KEY] || {};
        const updatedUnitId =
          meta.updatedUnitId || meta.updated_unit_id || meta.unitId;

        setData(simplifiedMessage);
        setNow(receivedAt);
        setUnitUpdatedAt((previous) => {
          const next = { ...previous };

          if (updatedUnitId) {
            next[String(updatedUnitId).toLowerCase()] = receivedAt;
            return next;
          }

          Object.keys(simplifiedMessage).forEach((key) => {
            if (key !== META_KEY) {
              next[String(key).toLowerCase()] = receivedAt;
            }
          });

          return next;
        });
        console.log("DATA",simplifiedMessage)
        console.log("Received At",receivedAt)
      }
    });

    socket.on("disconnect", () => {
      setConnected(false);
      setData(null);
      setUnitUpdatedAt({});
      console.log("Socketio disconnected")
    });

    return () => socket.disconnect();
  }, [dispensorId, station_id, gun1, gun2]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  const configGuns = selectedDispensor?.guns || [];
  const visibleData = data
    ? Object.fromEntries(
        Object.entries(data).filter(([key]) => key !== META_KEY),
      )
    : null;
  const isUnitLive = (unitId) => {
    const updatedAt = unitUpdatedAt[String(unitId || "").toLowerCase()];
    return Boolean(updatedAt && now - updatedAt <= LIVE_DATA_TIMEOUT_MS);
  };

  const matchedGuns = configGuns.map((gun) => {
    const gunKey = gun.gun_id; // "GUN1"

    const liveGunKey = Object.keys(visibleData || {}).find(
      (key) => key.toLowerCase() === gunKey.toLowerCase(),
    );

    return {
      ...gun,
      liveData:
        liveGunKey && isUnitLive(liveGunKey) ? visibleData[liveGunKey] : null,
    };
  });

  const handleStopCharging = (GunName) => {
    const socket = io(`${ipAddress}/StopCharging`, {
      transports: ["websocket","polling"],
    });
    socket.on("connect", () => {
      socket.emit("stop_charging", { GunId: GunName, stationId: station_id });
    });
    socket.on("connect_error", (err) => {
      console.error("Socket error:", err.message);
    });
  };

  // Separate dispensor + guns
  const dispensorData =
    visibleData && isUnitLive(dispensorId)
      ? Object.entries(visibleData).find(([key]) => key === dispensorId)
    : null;

  //  list
  const guns = visibleData
    ? Object.entries(visibleData).filter(
        ([key]) => key.toUpperCase().startsWith("GUN") && isUnitLive(key),
      )
    : [];
  return (
    <main className="dispenser-detail-shell hide-scrollbar">
      <div className="dispenser-detail-inner">
        <section className="dd-hero">
          <div className="dd-hero-image">
            <img src={image} alt="Dispenser" />
          </div>
          <div className="dd-hero-copy">
            <span className="dd-eyebrow">Live dispenser details</span>
            <h1>{dispensorId}</h1>
            <p>
              You are viewing real-time charger telemetry and gun status for
              this dispenser.
            </p>
          </div>

          <div
            className={`dd-status-pill ${connected ? "is-connected" : ""}`}
          >
            <span className="dd-status-dot" />
            <span className="dd-status-label">
              {connected ? "Connected" : "Disconnected"}
            </span>
          </div>
        </section>
        {/* GUN SUMMARY INFO */}
        <section className="dd-gun-summary-grid">
          {matchedGuns.map((gun, idx) => {
            const gunName = gun.gun_id;
            const gunData = gun.liveData;
            const moduleTemperature = getModuleTemperatureData(gunData);
            const metrics = buildGunMetrics(gunData);
            const soc = getLiveValue(gunData?.soc);
            const isCharging = gunData?.cpState === "state_c";
            return (
              <div
                key={idx}
                className="dd-card hide-scrollbar"
              >
                <div className="dd-card-header">
                  <div>
                    <span className="dd-card-kicker">Gun channel</span>
                    <h2>{gunName}</h2>
                  </div>
                  <span className={`dd-gun-chip ${isCharging ? "is-charging" : ""}`}>
                    {gunData?.cpState || "No live data"}
                  </span>
                </div>

                <div className="dd-soc-panel">
                  <div>
                    <span>Current SOC</span>
                    <strong>
                      {soc === undefined || soc === null || soc === ""
                        ? "N/A"
                        : `${formatValue(soc)}%`}
                    </strong>
                  </div>
                  <div className="dd-soc-track">
                    <span
                      style={{ width: `${Math.min(Number(soc) || 0, 100)}%` }}
                    />
                  </div>
                </div>

                <div className="dd-metric-grid">
                  {metrics.map((item) => (
                    <div className="dd-metric-tile" key={item.label}>
                      <span>{item.label}</span>
                      <strong>{item.value}</strong>
                    </div>
                  ))}
                </div>

                <div className="dd-metric-grid dd-legacy-metrics">
                  <>
                    {" "}
                    <p className="font-medium">Avg Module Temperature</p>
                    <p>
                      :{" "}
                      <span>
                        {formatModuleTemperatureValue(
                          moduleTemperature?.avgTemperature,
                        )}
                      </span>
                    </p>
                  </>
                  <p className="font-medium">Initial SOC (%)</p>
                  <p>
                    :{" "}
                    <span>
                      <DataValue data={gunData?.initialSoc} unit="%" />
                    </span>
                  </p>
                  <p className="font-medium">Current SOC</p>
                  <p>
                    : <DataValue data={gunData?.soc} unit="%" />
                  </p>
                  {(() => {
                    let powerMetrics = {};

                    try {
                      powerMetrics =
                        typeof gunData?.powerMetrics === "string"
                          ? JSON.parse(gunData.powerMetrics)
                          : gunData?.powerMetrics || {};
                    } catch {
                      powerMetrics = {};
                    }

                    return (
                      <>
                        <p className="font-medium">Demand Voltage</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.requested_voltage}
                            unit="V"
                          />
                        </p>
                        <p className="font-medium">Demand Current</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.requested_current}
                            unit="A"
                          />
                        </p>
                        <p className="font-medium">Demand Power</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.requested_power / 1000}
                            unit="kw"
                          />
                        </p>

                        <p className="font-medium">Charging Voltage</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.measured_voltage}
                            unit="V"
                          />
                        </p>
                        <p className="font-medium">Charging Current</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.measured_current}
                            unit="A"
                          />
                        </p>
                        <p className="font-medium">Charging Power</p>
                        <p>
                          :{" "}
                          <DataValue
                            data={powerMetrics?.measured_power / 1000}
                            unit="kw"
                          />
                        </p>
                        <p className="font-medium">Energy Consumption</p>
                        <p>: {formatNumber(powerMetrics?.energy, "kWh")}</p>
                      </>
                    );
                  })()}
                  <p className="font-medium">Cp State</p>
                  <p>
                    : <DataValue data={gunData?.cpState} unit="" />
                  </p>
                  <p className="font-medium">Duration (hh:mm)</p>{" "}
                  <p>
                    :{" "}
                    <span>
                      {gunData?.timeToFullSoc != null &&
                      gunData?.timeToFullSoc !== ""
                        ? formatSecondsToHHMM(gunData.timeToFullSoc)
                        : ""}
                    </span>
                  </p>
                  <>
                    {" "}
                    <p className="font-medium">Charging Authorization</p>{" "}
                    <p>
                      :{" "}
                      <DataValue
                        data={gunData?.chargingAuthorizationState}
                        unit="%"
                      />
                    </p>
                  </>
                  <p className="font-medium">Charging Session</p>{" "}
                  <p className="font-medium max-w-[350px] truncate">
                    : <DataValue data={gunData?.chargingSessionState} unit="" />
                  </p>
                  <p className="font-medium">Stop Reason Code</p>
                  <p>
                    :{" "}
                    {(() => {
                      try {
                        if (!gunData?.reportStatusInformation) return "";
                        const parsed = JSON.parse(
                          gunData.reportStatusInformation,
                        );
                        return parsed?.vendorErrorCode?.split(":")[1] || "";
                      } catch {
                        return "";
                      }
                    })()}
                  </p>
                  <p className="font-medium">Stop Reason Info</p>
                  <p>
                    :{" "}
                    {(() => {
                      try {
                        if (!gunData?.reportStatusInformation) return "";
                        const parsed = JSON.parse(
                          gunData.reportStatusInformation,
                        );
                        return parsed?.info || "";
                      } catch {
                        return "";
                      }
                    })()}
                  </p>
                </div>
                <ModuleTemperaturePanel
                  gunName={gunName}
                  moduleTemperature={moduleTemperature}
                />
                <div className="dd-stop-wrap">
                  <button
                    disabled={gunData?.cpState !== "state_c"}
                    className={`dd-stop-button ${
                      gunData?.cpState === "state_c"
                        ? "is-enabled"
                        : "is-disabled"
                    }`}
                    onClick={() => {
                      setSelectedGun(gunName);
                      setShowStopPopup(true);
                    }}
                  >
                    Stop Charging
                  </button>
                </div>
              </div>
            );
          })}
        </section>

        {/* DISPENSOR INFO */}
        {dispensorData && (
          <section className="dd-section hide-scrollbar">
            <div className="dd-section-header">
              <h2>{dispensorData[0]}</h2>

              <button
                className="dd-toggle-button"
                onClick={() => setShowDispensor((prev) => !prev)}
              >
                <span
                  className={`inline-block transition-transform duration-300 ${
                    showDispensor ? "rotate-0" : "rotate-180"
                  }`}
                >
                  <FaAngleUp />
                </span>
              </button>
            </div>

            {showDispensor && (
              <DataTable rows={getTableRows(dispensorData[1])} />
            )}
          </section>
        )}
        {/* GUNS INFO */}
        <section className="dd-guns-section">
          {/* Heading TOP CENTER */}
          <div className="dd-section-header">
            <h2>Guns Info</h2>
            <button
              className="dd-toggle-button"
              onClick={() => setShowGuns((prev) => !prev)}
            >
              <span
                className={`inline-block transition-transform duration-300 ${
                  showGuns ? "rotate-0" : "rotate-180"
                }`}
              >
                <FaAngleUp />
              </span>
            </button>
          </div>

          {/* Tables Row */}
          {showGuns && (
            <div className="dd-gun-table-grid hide-scrollbar">
              {guns.length > 0 ? (
                guns.map(([gunName, gunData], idx) => (
                  <div key={idx} className="dd-gun-table-card hide-scrollbar">
                    <div className="dd-card-header dd-table-card-header">
                      <span className="dd-gun-name-badge">{gunName}</span>
                    </div>

                    <DataTable rows={getTableRows(gunData)} />
                  </div>
                ))
              ) : (
                <div className="dd-empty">No gun telemetry received yet</div>
              )}
            </div>
          )}
        </section>
      </div>
      {/* {isStale && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 pointer-events-none">
          <div className="pointer-events-auto mt-4 bg-yellow-100 border border-yellow-400 text-yellow-800 px-4 py-2 rounded-lg text-center font-medium">
            No recent updates from gateway. Data may be outdated.
            {lastUpdate && (
              <p className="text-xs text-gray-500 mt-2 text-center">
                Last update: {formatTime(lastUpdate)}
              </p>
            )}
          </div>
        </div>
      )} */}
      {showStopPopup && (
        <div className="dd-modal-backdrop">
          <div className="dd-modal">
            <h2>Confirm Stop Charging?</h2>
            <p>
              Do you want to stop charging for this <b>{selectedGun}</b>?
            </p>

            <div className="dd-modal-actions">
              <button
                className="dd-modal-secondary"
                onClick={() => setShowStopPopup(false)}
              >
                Cancel
              </button>

              <button
                className="dd-modal-danger"
                onClick={() => {
                  handleStopCharging(selectedGun);
                  setShowStopPopup(false);
                }}
              >
                Yes, Stop
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

const getLiveValue = (data) =>
  typeof data === "object" && data !== null && "value" in data
    ? data.value
    : data;

const parseObjectValue = (value) => {
  const rawValue = getLiveValue(value);
  if (!rawValue) return {};
  if (typeof rawValue === "object") return rawValue;
  try {
    return JSON.parse(rawValue);
  } catch {
    return {};
  }
};

const getPowerMetrics = (gunData) => parseObjectValue(gunData?.powerMetrics);

const getStopInfo = (gunData) => {
  const parsed = parseObjectValue(gunData?.reportStatusInformation);
  return {
    code: parsed?.vendorErrorCode?.split(":")[1] || "",
    info: parsed?.info || "",
  };
};

const getModuleTemperatureData = (gunData) => {
  const raw = parseObjectValue(
    gunData?.moduleTemperatures ||
      gunData?.moduleTemperature ||
      gunData?.modulesTemperature,
  );

  if (!raw || Object.keys(raw).length === 0) {
    return {
      gun: null,
      avgTemperature: null,
      modules: [],
    };
  }

  return {
    gun: raw.gun,
    avgTemperature: raw.avg_temperature ?? raw.avgTemperature,
    modules: getModuleTemperatureRows(raw),
  };
};

const MODULE_TEMPERATURE_META_KEYS = new Set([
  "gun",
  "avg_temperature",
  "avgTemperature",
  "modules",
]);

const getModuleTemperatureValue = (value) => {
  const liveValue = getLiveValue(value);

  if (liveValue && typeof liveValue === "object") {
    return (
      liveValue.temperature ??
      liveValue.temp ??
      liveValue.value ??
      liveValue.avg_temperature ??
      liveValue.avgTemperature
    );
  }

  return liveValue;
};

const getModuleTemperatureRows = (raw) => {
  const rows = [];

  const addRow = (moduleName, temperature) => {
    if (moduleName === undefined || moduleName === null || moduleName === "") {
      return;
    }

    rows.push({
      moduleName: String(moduleName),
      temperature: getModuleTemperatureValue(temperature),
    });
  };

  if (Array.isArray(raw.modules)) {
    raw.modules.forEach((item, index) => {
      if (item && typeof item === "object") {
        const moduleName =
          item.module_name ??
          item.moduleName ??
          item.key ??
          item.name ??
          `module${index + 1}`;
        const temperature =
          item.temperature ?? item.temp ?? item.value ?? item[moduleName];

        addRow(moduleName, temperature);
        return;
      }

      addRow(`module${index + 1}`, item);
    });
  } else if (raw.modules && typeof raw.modules === "object") {
    Object.entries(raw.modules).forEach(([moduleName, temperature]) => {
      addRow(moduleName, temperature);
    });
  }

  if (rows.length === 0) {
    Object.entries(raw).forEach(([moduleName, temperature]) => {
      if (!MODULE_TEMPERATURE_META_KEYS.has(moduleName)) {
        addRow(moduleName, temperature);
      }
    });
  }

  return rows;
};

const metric = (label, value, unit = "") => ({
  label,
  value: value === undefined || value === null || value === "" ? "N/A" : formatNumber(value, unit),
});

const buildGunMetrics = (gunData) => {
  const powerMetrics = getPowerMetrics(gunData);
  const stopInfo = getStopInfo(gunData);
  const moduleTemperature = getModuleTemperatureData(gunData);

  return [
    metric(
      "Avg module temperature",
      moduleTemperature.avgTemperature,
      TEMP_UNIT,
    ),
    metric("Initial SOC", getLiveValue(gunData?.initialSoc), "%"),
    metric("Current SOC", getLiveValue(gunData?.soc), "%"),
    metric("Demand voltage", powerMetrics?.requested_voltage, "V"),
    metric("Demand current", powerMetrics?.requested_current, "A"),
    metric("Demand power", powerMetrics?.requested_power ? powerMetrics.requested_power / 1000 : "", "kW"),
    metric("Charging voltage", powerMetrics?.measured_voltage, "V"),
    metric("Charging current", powerMetrics?.measured_current, "A"),
    metric("Charging power", powerMetrics?.measured_power ? powerMetrics.measured_power / 1000 : "", "kW"),
    metric("CP state", getLiveValue(gunData?.cpState)),
    metric(
      "Duration",
      gunData?.timeToFullSoc != null && gunData?.timeToFullSoc !== ""
        ? formatSecondsToHHMM(getLiveValue(gunData.timeToFullSoc))
        : "",
    ),
    metric("Authorization", getLiveValue(gunData?.chargingAuthorizationState)),
    metric("Charging session", getLiveValue(gunData?.chargingSessionState)),
    metric("Stop reason code", stopInfo.code),
    metric("Stop reason info", stopInfo.info),
    metric("Energy consumption", powerMetrics?.energy, "kWh"),
  ];
};

const formatModuleTemperatureValue = (value) =>
  value === undefined || value === null || value === ""
    ? "N/A"
    : formatNumber(value, TEMP_UNIT) || "N/A";

const ModuleTemperaturePanel = ({ gunName, moduleTemperature }) => {
  const tableRows =
    moduleTemperature?.modules?.length > 0
      ? moduleTemperature.modules
      : [{ moduleName: "N/A", temperature: null }];

  return (
    <section className="dd-module-temp-panel" aria-label="Module temperatures">
      <div className="dd-module-temp-header">
        <div>
          <span>Avg module temperature</span>
          <strong>
            {formatModuleTemperatureValue(moduleTemperature?.avgTemperature)}
          </strong>
        </div>
        {(moduleTemperature?.gun || gunName) && (
          <span className="dd-module-temp-gun">
            {moduleTemperature?.gun || gunName}
          </span>
        )}
      </div>

      <div className="dd-module-temp-table-wrap">
        <table className="dd-module-temp-table">
          <thead>
            <tr>
              <th>Module</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((item, index) => (
              <tr key={`${item.moduleName}-${index}`}>
                <td>{item.moduleName || "N/A"}</td>
                <td>{formatModuleTemperatureValue(item.temperature)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

const getTableRows = (source) =>
  Object.entries(source || {})
    .filter(([, value]) => value !== undefined)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({
      key,
      label: formatLabel(key),
      value: formatValue(value),
    }));

const DataTable = ({ rows }) => (
  <div className="dd-data-table-wrap">
    <table className="dd-data-table">
      <thead>
        <tr>
          <th>Parameter</th>
          <th>Live Value</th>
        </tr>
      </thead>
      <tbody>
        {rows.length > 0 ? (
          rows.map((row) => (
            <tr key={row.key}>
              <td data-label="Parameter">{row.label}</td>
              <td data-label="Live Value">{row.value}</td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="2">No live data available</td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

export default DispenserDetail;
